/**
 * Direct App Hosting Call Detector
 * 
 * Utility to detect and warn about direct calls to App Hosting domain (.hosted.app)
 * that bypass Firebase Hosting rewrites. These calls can cause cookie issues
 * and should be avoided.
 * 
 * Terminology:
 * - Firebase Hosting = Static hosting (.web.app, .firebaseapp.com) - rewrites /api/* to App Hosting
 * - App Hosting = Cloud Run backend (.hosted.app) - direct backend access
 */

import logger from '@/lib/logging';
import { getCurrentEnvironment, getAppHostingUrl } from '@/config/environment-config';

/**
 * Known locations that might make direct API calls
 * This list helps developers identify where direct calls might be happening
 */
export const POTENTIAL_DIRECT_CALL_LOCATIONS = [
  'src/lib/api-client.ts',
  'src/contexts/AuthContext.tsx',
  'src/lib/activity-logger.ts',
  'src/components/agent/lib/agent-tools.ts',
  'src/components/feedback-button/index.tsx',
  'src/services/api/agent-api.ts',
  'src/components/agent/services/agent-api.ts',
] as const;

/**
 * Check if a URL is a direct call to App Hosting
 */
export function isDirectAppHostingCall(url: string): boolean {
  if (typeof window === 'undefined') {
    return false; // Server-side, not applicable
  }

  try {
    const env = getCurrentEnvironment();
    const appHostingUrl = getAppHostingUrl(env);
    
    // Check if URL contains app hosting domain
    return url.includes(appHostingUrl) || url.includes('.hosted.app');
  } catch (e) {
    return false;
  }
}

/**
 * Check if current hostname is Firebase Hosting (static hosting)
 * 
 * Firebase Hosting domains:
 * - *.web.app
 * - *.firebaseapp.com
 * 
 * These are NOT App Hosting (.hosted.app) which is the Cloud Run backend.
 */
export function isFirebaseHosting(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  const hostname = window.location.hostname;
  return hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com');
}

/**
 * Warn about direct app hosting calls
 */
export function warnDirectAppHostingCall(
  url: string,
  context?: {
    file?: string;
    function?: string;
    line?: number;
  }
): void {
  if (typeof window === 'undefined') {
    return; // Server-side, not applicable
  }

  const hostname = window.location.hostname;
  const isFirebaseHost = isFirebaseHosting();
  
  if (!isFirebaseHost) {
    return; // Not on Firebase Hosting, direct calls are OK
  }

  if (!isDirectAppHostingCall(url)) {
    return; // Not a direct call
  }

  try {
    const env = getCurrentEnvironment();
    const appHostingUrl = getAppHostingUrl(env);

    logger.warn('⚠️ [DIRECT_CALL] Direct App Hosting call detected from Firebase Hosting', {
      category: 'api',
      url,
      hostname,
      appHostingUrl,
      context,
      message: 'This call bypasses Firebase Hosting rewrites and may cause cookie issues.',
      recommendation: 'Use relative URLs (e.g., /api/...) instead of absolute URLs to App Hosting domain (.hosted.app)',
      impact: [
        'Cookies may not be sent due to cross-origin restrictions',
        'Session authentication may fail',
        'CORS errors may occur',
      ],
      fix: `Change "${url}" to a relative URL like "/api/..." to use Firebase Hosting rewrites`,
      note: 'Firebase Hosting (.web.app, .firebaseapp.com) rewrites /api/* to App Hosting (.hosted.app)',
      potentialLocations: POTENTIAL_DIRECT_CALL_LOCATIONS,
    });
  } catch (e) {
    // Ignore errors in detection
  }
}

/**
 * Get guidance for fixing direct app hosting calls
 */
export function getDirectCallGuidance(): string {
  return `
Direct App Hosting Call Detection

If you see warnings about direct app hosting calls, here's how to fix them:

1. **Problem**: Making absolute URLs to App Hosting domain (.hosted.app) from Firebase Hosting
   - Example: https://iowa--harry-playground-x8x90.us-central1.hosted.app/api/...
   - Firebase Hosting = static hosting (.web.app, .firebaseapp.com)
   - App Hosting = Cloud Run backend (.hosted.app)
   
2. **Solution**: Use relative URLs instead
   - Change to: /api/...
   - Firebase Hosting will automatically rewrite these to App Hosting via firebase.json

3. **Why**: 
   - Cookies are scoped to the domain that sets them
   - Firebase Hosting (.web.app, .firebaseapp.com) and App Hosting (.hosted.app) are different domains
   - Direct calls bypass the rewrite mechanism
   - Cookies won't be sent in cross-origin requests

4. **Common locations to check**:
   ${POTENTIAL_DIRECT_CALL_LOCATIONS.map(loc => `   - ${loc}`).join('\n')}

5. **How to verify**:
   - Check browser Network tab
   - Look for requests to .hosted.app domains (should NOT see these from Firebase Hosting)
   - Should only see requests to current domain (.web.app or .firebaseapp.com)
   - Firebase Hosting rewrites /api/* requests to App Hosting automatically
  `.trim();
}
